import React, { useState, useRef, useEffect } from 'react';
import { trackDocxImportCancelled, trackDocxImportConfirmed, trackDocxSectionMerged, trackDocxSectionRenamed, trackDocxSectionTypeChanged, trackDocxSectionsReordered, trackDocxImportUndoUsed } from '../../../../../utils/analytics';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragMoveEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Section } from '../../../../NotionEditor/types';

interface DocxImportModalProps {
  sections: Section[];
  isImporting: boolean;
  importStatus?: string;
  onConfirm: (sections: Section[]) => void;
  onCancel: () => void;
}

interface SortableRowProps {
  section: Section;
  index: number;
  totalSections: number;
  onChangeType: (id: string, type: Section['type']) => void;
  onRename: (id: string, title: string) => void;
  onRenameBlur: () => void;
  onMerge: (id: string) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({ section, index, totalSections, onChangeType, onRename, onRenameBlur, onMerge }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing shrink-0 touch-none"
        title="Drag to reorder"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <circle cx="9" cy="6"  r="1.5"/><circle cx="15" cy="6"  r="1.5"/>
          <circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
          <circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/>
        </svg>
      </button>
      <select
        value={section.type}
        onChange={e => onChangeType(section.id, e.target.value as Section['type'])}
        title="Change section type"
        className={`text-xs font-medium px-2 py-0.5 rounded-full border shrink-0 cursor-pointer appearance-none focus:outline-none ${TYPE_STYLE[section.type]}`}
      >
        <option value="front-matter">Front Matter</option>
        <option value="chapter">Chapter</option>
        <option value="end-matter">End Matter</option>
      </select>
      <input
        type="text"
        value={section.title}
        onChange={e => onRename(section.id, e.target.value)}
        onBlur={onRenameBlur}
        className="flex-1 bg-white border border-gray-200 rounded px-2.5 py-1.5 text-sm text-gray-900 focus:outline-none focus:border-orange-500"
      />
      <button
        onClick={() => onMerge(section.id)}
        title={index === 0 ? 'Merge into next section' : 'Merge into previous section'}
        className="text-gray-400 hover:text-orange-500 transition-colors shrink-0"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      </button>
    </div>
  );
};

const TYPE_STYLE: Record<Section['type'], string> = {
  'front-matter': 'bg-blue-100 text-blue-700 border-blue-200',
  'chapter':      'bg-green-100 text-green-700 border-green-200',
  'end-matter':   'bg-purple-100 text-purple-700 border-purple-200',
};

const DocxImportModal: React.FC<DocxImportModalProps> = ({
  sections: initial,
  isImporting,
  importStatus,
  onConfirm,
  onCancel,
}) => {
  // History stack for full undo/redo support
  const [history, setHistory] = useState<Section[][]>([initial]);
  const [cursor, setCursor] = useState(0);

  const sections = history[cursor];
  const canUndo = cursor > 0;
  const canRedo = cursor < history.length - 1;

  const commit = (next: Section[]) => {
    // Discard any redo states ahead of the cursor before pushing new state
    const trimmed = history.slice(0, cursor + 1);
    setHistory([...trimmed, next]);
    setCursor(trimmed.length);
  };

  const undo = () => { if (canUndo) { setCursor(c => c - 1); trackDocxImportUndoUsed(); } };
  const redo = () => { if (canRedo) setCursor(c => c + 1); };

  const renameSection = (id: string, title: string) => {
    commit(sections.map(s => s.id === id ? { ...s, title } : s));
  };

  const scrollableRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const modalOpenedAt = useRef<number>(Date.now());
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Manual scroll zone: when dragging within 60px of the top/bottom edge of the
  // scrollable div, scroll it at a controlled speed using rAF.
  // Stops when pointer leaves the zone OR the container reaches its scroll boundary.
  const startEdgeScroll = (clientY: number) => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    const el = scrollableRef.current;
    if (!el) return;

    const { top, bottom } = el.getBoundingClientRect();
    const ZONE = 60;
    const distTop = clientY - top;
    const distBottom = bottom - clientY;

    let speed = 0;
    if (distTop < ZONE && distTop > 0) speed = -(1 - distTop / ZONE) * 12;
    else if (distBottom < ZONE && distBottom > 0) speed = (1 - distBottom / ZONE) * 12;

    if (speed === 0) { rafRef.current = null; return; }

    // Stop at scroll boundaries — prevents infinite scroll past the list end
    const reachedBottom = speed > 0 && el.scrollTop >= el.scrollHeight - el.clientHeight;
    const reachedTop = speed < 0 && el.scrollTop <= 0;
    if (reachedBottom || reachedTop) { rafRef.current = null; return; }

    el.scrollTop += speed;
    rafRef.current = requestAnimationFrame(() => startEdgeScroll(clientY));
  };

  const stopEdgeScroll = () => {
    if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  };

  useEffect(() => stopEdgeScroll, []);

  const handleDragMove = (event: DragMoveEvent) => {
    const initial = event.activatorEvent as PointerEvent;
    startEdgeScroll(initial.clientY + event.delta.y);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    stopEdgeScroll();
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sections.findIndex(s => s.id === active.id);
    const newIndex = sections.findIndex(s => s.id === over.id);
    commit(arrayMove(sections, oldIndex, newIndex));
    trackDocxSectionsReordered();
  };

  const changeType = (id: string, type: Section['type']) => {
    const prev = sections.find(s => s.id === id)?.type ?? 'chapter';
    commit(sections.map(s => s.id === id ? { ...s, type } : s));
    trackDocxSectionTypeChanged(prev, type);
  };

  const mergeSection = (id: string) => {
    const idx = sections.findIndex(s => s.id === id);
    if (idx === -1) return;
    const next = [...sections];
    if (idx === 0 && next.length > 1) {
      next[1] = { ...next[1], content: next[0].content + next[1].content };
      next.splice(0, 1);
    } else if (idx > 0) {
      next[idx - 1] = { ...next[idx - 1], content: next[idx - 1].content + next[idx].content };
      next.splice(idx, 1);
    }
    commit(next);
    trackDocxSectionMerged();
  };

  const chapterCount = sections.filter(s => s.type === 'chapter').length;
  const frontCount   = sections.filter(s => s.type === 'front-matter').length;
  const endCount     = sections.filter(s => s.type === 'end-matter').length;

  const summary = [
    chapterCount > 0 && `${chapterCount} chapter${chapterCount > 1 ? 's' : ''}`,
    frontCount > 0   && `${frontCount} front matter`,
    endCount > 0     && `${endCount} end matter`,
  ].filter(Boolean).join(', ');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">

        <div className="p-6 border-b border-gray-200">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Import manuscript</h2>
              <p className="text-sm text-gray-500 mt-1">
                {sections.length} section{sections.length !== 1 ? 's' : ''} detected
                {summary && ` — ${summary}`}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Rename sections or merge unwanted ones into the previous section.
              </p>
            </div>
            {/* Undo / Redo */}
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={undo}
                disabled={!canUndo}
                title="Undo"
                className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
              </button>
              <button
                onClick={redo}
                disabled={!canRedo}
                title="Redo"
                className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a8 8 0 00-8 8v2m18-10l-6 6m6-6l-6-6" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div ref={scrollableRef} className="overflow-y-auto flex-1 p-6 space-y-2">
          {sections.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No sections to import.</p>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragMove={handleDragMove}
              onDragEnd={handleDragEnd}
              autoScroll={false}
            >
              <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                {sections.map((section, i) => (
                  <SortableRow
                    key={section.id}
                    section={section}
                    index={i}
                    totalSections={sections.length}
                    onChangeType={changeType}
                    onRename={renameSection}
                    onRenameBlur={trackDocxSectionRenamed}
                    onMerge={mergeSection}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={() => {
              trackDocxImportCancelled({ sections_count: sections.length, time_in_modal_ms: Date.now() - modalOpenedAt.current });
              onCancel();
            }}
            disabled={isImporting}
            className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <div className="flex flex-col items-end gap-1">
            {isImporting && importStatus && (
              <p className="text-xs text-gray-500">{importStatus}</p>
            )}
            <button
              onClick={() => {
                const fixed = sections.map(s => ({
                  ...s,
                  title: s.title.trim() || 'Untitled Section',
                }));
                trackDocxImportConfirmed({ sections_count: fixed.length, time_in_modal_ms: Date.now() - modalOpenedAt.current });
                onConfirm(fixed);
              }}
              disabled={isImporting || sections.length === 0}
              className="px-5 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
            >
              {isImporting ? 'Importing…' : 'Import book'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DocxImportModal;
